import React, { useState, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { Student } from '../types';

interface StudentListProps {
  students: Student[];
  onDragStart?: (student: Student) => void;
  onAddressClick?: (address: string, studentName: string, type: 'pickup' | 'dropoff') => void;
  onEdit?: (student: Student) => void;
}

const StudentList: React.FC<StudentListProps> = ({
  students,
  onDragStart,
  onAddressClick,
  onEdit
}) => {
  const [showTooltip, setShowTooltip] = useState<{[key: string]: boolean}>({});
  const studentRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  const calculateAge = (birthYear: number) => {
    const currentYear = new Date().getFullYear();
    return currentYear - birthYear;
  };

  // 툴팁 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(studentRefs.current).forEach(studentId => {
        if (studentRefs.current[studentId] && !studentRefs.current[studentId]?.contains(event.target as Node)) {
          setShowTooltip(prev => ({...prev, [studentId]: false}));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStudentClick = (studentId: string) => {
    setShowTooltip(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  return (
    <div className="space-y-4">
      {students.map(student => {
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
          id: student.id,
          data: { student }
        });

        const style = transform ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          zIndex: isDragging ? 1000 : undefined,
        } : undefined;

        return (
          <div
            key={student.id}
            ref={(node) => {
              studentRefs.current[student.id] = node;
              setNodeRef(node);
            }}
            style={style}
            {...listeners}
            {...attributes}
            className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow ${
              isDragging ? 'opacity-50' : ''
            }`}
            onClick={() => handleStudentClick(student.id)}
          >
            <div className="p-4">
              <div className="text-sm font-medium">{student.name}</div>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div>{student.school}</div>
                <div>{calculateAge(student.birth_year)}세</div>
              </div>

              {showTooltip[student.id] && (
                <div
                  className="absolute z-50 w-64 p-3 bg-gray-800 text-white rounded-lg shadow-lg"
                  style={{
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '8px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="space-y-2 text-sm">
                    <p><strong>연락처:</strong> {student.student_phone || '-'}</p>
                    <p><strong>부모님 연락처:</strong> {student.parent_phone}</p>
                    <div className="space-y-1">
                      <p><strong>등원 주소:</strong></p>
                      <button
                        className="text-blue-300 text-left hover:text-blue-200"
                        onClick={() => onAddressClick?.(student.pickup_address1, student.name, 'pickup')}
                      >
                        {student.pickup_address1}
                      </button>
                      {student.pickup_address2 && (
                        <button
                          className="text-blue-300 text-left hover:text-blue-200"
                          onClick={() => onAddressClick?.(student.pickup_address2, student.name, 'pickup')}
                        >
                          {student.pickup_address2}
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      <p><strong>하원 주소:</strong></p>
                      <button
                        className="text-blue-300 text-left hover:text-blue-200"
                        onClick={() => onAddressClick?.(student.dropoff_address1, student.name, 'dropoff')}
                      >
                        {student.dropoff_address1}
                      </button>
                      {student.dropoff_address2 && (
                        <button
                          className="text-blue-300 text-left hover:text-blue-200"
                          onClick={() => onAddressClick?.(student.dropoff_address2, student.name, 'dropoff')}
                        >
                          {student.dropoff_address2}
                        </button>
                      )}
                    </div>
                    {student.notes && (
                      <div>
                        <p><strong>메모:</strong></p>
                        <p className="whitespace-pre-line">{student.notes}</p>
                      </div>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(student)}
                        className="mt-2 w-full px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        수정
                      </button>
                    )}
                  </div>

                  {/* Tooltip Arrow */}
                  <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
                    <div className="border-8 border-transparent border-t-gray-800" />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface DraggableStudentProps {
  student: Student;
  onDragStart?: (student: Student) => void;
  onAddressClick?: (address: string, studentName: string, type: 'pickup' | 'dropoff') => void;
  onEdit?: (student: Student) => void;
}

const DraggableStudent: React.FC<DraggableStudentProps> = ({
  student,
  onDragStart,
  onAddressClick,
  onEdit
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const studentRef = useRef<HTMLDivElement>(null);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: student.id,
    data: { student }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : undefined,
    position: 'relative',
    touchAction: 'none'
  } : undefined;

  useEffect(() => {
    if (isDragging && onDragStart) {
      onDragStart(student);
    }
  }, [isDragging, student, onDragStart]);

  const calculateAge = (birthYear: number) => {
    const currentYear = new Date().getFullYear();
    return currentYear - birthYear;
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  // 툴팁 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (studentRef.current && !studentRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={(node) => {
        studentRef.current = node;
        setNodeRef(node);
      }}
      style={style}
      {...listeners}
      {...attributes}
      className={`relative p-3 bg-white rounded-lg shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow select-none ${
        isDragging ? 'opacity-50' : ''
      }`}
      onClick={handleClick}
    >
      <div className="text-sm font-medium">{student.name}</div>
      <div className="text-xs text-gray-500 space-y-0.5">
        <div>{student.school}</div>
        <div>{calculateAge(student.birth_year)}세</div>
      </div>

      {showTooltip && (
        <div
          className="absolute z-50 w-64 p-3 bg-gray-800 text-white rounded-lg shadow-lg"
          style={{
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-2 text-sm">
            <p><strong>연락처:</strong> {student.student_phone || '-'}</p>
            <p><strong>부모님 연락처:</strong> {student.parent_phone}</p>
            <div className="space-y-1">
              <p><strong>등원 주소:</strong></p>
              <button
                className="text-blue-300 text-left hover:text-blue-200"
                onClick={() => onAddressClick?.(student.pickup_address1, student.name, 'pickup')}
              >
                {student.pickup_address1}
              </button>
              {student.pickup_address2 && (
                <button
                  className="text-blue-300 text-left hover:text-blue-200"
                  onClick={() => onAddressClick?.(student.pickup_address2, student.name, 'pickup')}
                >
                  {student.pickup_address2}
                </button>
              )}
            </div>
            <div className="space-y-1">
              <p><strong>하원 주소:</strong></p>
              <button
                className="text-blue-300 text-left hover:text-blue-200"
                onClick={() => onAddressClick?.(student.dropoff_address1, student.name, 'dropoff')}
              >
                {student.dropoff_address1}
              </button>
              {student.dropoff_address2 && (
                <button
                  className="text-blue-300 text-left hover:text-blue-200"
                  onClick={() => onAddressClick?.(student.dropoff_address2, student.name, 'dropoff')}
                >
                  {student.dropoff_address2}
                </button>
              )}
            </div>
            {student.notes && (
              <div>
                <p><strong>메모:</strong></p>
                <p className="whitespace-pre-line">{student.notes}</p>
              </div>
            )}
            {onEdit && (
              <button
                onClick={() => onEdit(student)}
                className="mt-2 w-full px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                수정
              </button>
            )}
          </div>

          {/* Tooltip Arrow */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
            <div className="border-8 border-transparent border-t-gray-800" />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;